import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Logo,
  MainBody,
  Title,
  Activity,
  Pitch,
  InfoTitle,
  InfoValue,
  ButtonWrapper,
  ViewProfileButton,
  SelectButton,
  ActionsWrapper,
  TopRow,
  BottomRow,
  LogoColumn,
  TopInfoColumn,
  TextCol,
  ButtonCol,
  InfoCol,
  Divider,
  ActionsButton,
  ActionsDropdown,
  ActionsDropdownButton,
  Titles,
  EcosystemBadge,
} from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { defaultStartupLogo } from './behavior';

export interface TeiaStartupCardProps {
  title?: string;
  pitch?: string;
  logo?: string;
  activity?: string;
  numberEmployees?: string;
  businessModel?: string;
  target?: string;
  state?: string;
  isInMyEcosystem?: boolean;
  badgeLabel?: string;
  variant?: 'default' | 'public-administration';
  dirigenteMaximo?: string;
  numeroServidores?: string;
  areaInovacao?: string;
  onViewProfile?: () => void;
  hideViewProfileButton?: boolean;
  onAddResident?: () => void;
  onRemoveResident?: () => void;
  onConnect?: () => void;
  onSelect?: () => void;
  onToggleHidden?: () => void;
  isHidden?: boolean;
  trailingSlot?: React.ReactNode;
}

export const TeiaNewStartupCard = ({
  title,
  pitch,
  logo,
  activity,
  numberEmployees,
  businessModel,
  target,
  state: addressState,
  isInMyEcosystem = false,
  badgeLabel,
  variant = 'default',
  dirigenteMaximo,
  numeroServidores,
  areaInovacao,
  onViewProfile,
  hideViewProfileButton = false,
  onAddResident,
  onRemoveResident,
  onConnect,
  onSelect,
  onToggleHidden,
  isHidden = false,
  trailingSlot,
}: TeiaStartupCardProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionsClick = () => setIsDropdownOpen((prev) => !prev);

  const showResidentActions =
    onAddResident != null && onRemoveResident != null;
  const showConnect =
    variant !== 'public-administration' && onConnect != null;
  const showVisibilityToggle = onToggleHidden != null;
  const showActionsMenu = showResidentActions || showConnect || showVisibilityToggle;

  const handleAddOrRemoveResident = () => {
    if (isInMyEcosystem) onRemoveResident?.();
    else onAddResident?.();
    setIsDropdownOpen(false);
  };

  const handleConnect = () => {
    onConnect?.();
    setIsDropdownOpen(false);
  };

  const handleToggleHidden = () => {
    onToggleHidden?.();
    setIsDropdownOpen(false);
  };

  const formatEmployees = (value?: string) => {
    if (!value) return '-';
    return value.includes('funcionários') || value.includes('colaboradores')
      ? value
      : `${value} funcionários`;
  };

  const handleCardClick = () => onViewProfile?.();
  const isCardInteractive = onViewProfile != null;

  return (
    <Card
      $interactive={isCardInteractive}
      $isHidden={isHidden}
      onClick={isCardInteractive ? handleCardClick : undefined}
      role={isCardInteractive ? 'button' : undefined}
      tabIndex={isCardInteractive ? 0 : undefined}
      onKeyDown={isCardInteractive ? (e) => e.key === 'Enter' && handleCardClick() : undefined}
    >
      <MainBody>
        <TopRow>
          <LogoColumn>
            <Logo src={logo || defaultStartupLogo} alt={title || 'Startup logo'} />
          </LogoColumn>
          <TopInfoColumn>
            <Titles>
              <Title>{title || '-'}</Title>
              {badgeLabel != null && badgeLabel !== '' && (
                <EcosystemBadge>{badgeLabel}</EcosystemBadge>
              )}
            </Titles>
            <Activity>{activity || '-'}</Activity>
            <Pitch>{pitch || '-'}</Pitch>
          </TopInfoColumn>
        </TopRow>

        <Divider />

        <BottomRow>
          <TextCol>
            {variant === 'public-administration' ? (
              <>
                <InfoCol>
                  <InfoTitle>Dirigente máximo</InfoTitle>
                  <InfoValue>{dirigenteMaximo || '-'}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>Número de servidores</InfoTitle>
                  <InfoValue>{numeroServidores || '-'}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>Área de inovação</InfoTitle>
                  <InfoValue>{areaInovacao || '-'}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>Estado</InfoTitle>
                  <InfoValue>{addressState || '-'}</InfoValue>
                </InfoCol>
              </>
            ) : (
              <>
                <InfoCol>
                  <InfoTitle>Modelo de negócio</InfoTitle>
                  <InfoValue>{businessModel || '-'}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>Público alvo</InfoTitle>
                  <InfoValue>{target || '-'}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>N° colaboradores</InfoTitle>
                  <InfoValue>{formatEmployees(numberEmployees)}</InfoValue>
                </InfoCol>
                <InfoCol>
                  <InfoTitle>Estado</InfoTitle>
                  <InfoValue>{addressState || '-'}</InfoValue>
                </InfoCol>
              </>
            )}
          </TextCol>

          <ButtonCol onClick={(e) => e.stopPropagation()}>
            <ActionsWrapper ref={dropdownRef}>
              <ButtonWrapper>
                {onViewProfile != null && !hideViewProfileButton && (
                  <ViewProfileButton onClick={onViewProfile}>Ver perfil</ViewProfileButton>
                )}
                {onSelect != null && (
                  <SelectButton onClick={onSelect}>Selecionar</SelectButton>
                )}
                {trailingSlot}
                {showActionsMenu && (
                  <ActionsButton onClick={handleActionsClick}>
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </ActionsButton>
                )}
              </ButtonWrapper>
              {showActionsMenu && isDropdownOpen && (
                <ActionsDropdown>
                  {showVisibilityToggle && (
                    <ActionsDropdownButton onClick={handleToggleHidden}>
                      {isHidden ? 'Ativar startup' : 'Ocultar startup'}
                    </ActionsDropdownButton>
                  )}
                  {showResidentActions && (
                    <ActionsDropdownButton onClick={handleAddOrRemoveResident}>
                      {isInMyEcosystem ? 'Remover como residente' : 'Adicionar como residente'}
                    </ActionsDropdownButton>
                  )}
                  {showConnect && (
                    <ActionsDropdownButton onClick={handleConnect}>
                      Conectar
                    </ActionsDropdownButton>
                  )}
                </ActionsDropdown>
              )}
            </ActionsWrapper>
          </ButtonCol>
        </BottomRow>
      </MainBody>
    </Card>
  );
};
